import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, Sparkles, Search, Compass, MapPin, MessageCircle, Mail, Loader2 } from "lucide-react";
import SEO from "../../components/SEO";
import { api } from "../../lib/api";
import { CONTACT, track } from "../../lib/institutional";

function PageHero({ eyebrow, title, subtitle }) {
  return (
    <section className="bg-slate-900 text-white pt-28 pb-14">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <p className="text-xs font-semibold tracking-widest uppercase text-primary/90 mb-2">{eyebrow}</p>
        <h1 className="font-heading font-extrabold text-3xl md:text-5xl">{title}</h1>
        {subtitle && <p className="text-white/80 mt-4 max-w-2xl">{subtitle}</p>}
      </div>
    </section>
  );
}

export function Sobre() {
  const navigate = useNavigate();
  return (
    <div data-testid="page-sobre">
      <SEO title="Sobre" description="Conheça o Guia de Jampa AI, a plataforma inteligente que conecta pessoas, empresas e experiências em João Pessoa/PB." path="/sobre" />
      <PageHero eyebrow="Sobre" title="A inteligência urbana de João Pessoa" subtitle="O Guia de Jampa AI reúne, em um só lugar, tudo o que acontece na cidade — com dados reais e inteligência artificial." />
      <section className="max-w-3xl mx-auto px-4 md:px-8 py-14 space-y-5 text-slate-600 leading-relaxed">
        <p>O Guia de Jampa AI nasceu para conectar moradores, turistas e empresas às melhores experiências de João Pessoa. Reunimos restaurantes, hotéis, praias, eventos, promoções, turismo e serviços em uma plataforma única, inteligente e fácil de usar.</p>
        <p>Nossa Inteligência Artificial responde perguntas sobre a cidade utilizando informações reais da plataforma: empresas cadastradas, eventos, notícias e dados locais. Assim, você descobre o que fazer, onde ir e quem contratar com muito mais facilidade.</p>
        <p>Para as empresas, oferecemos presença profissional, visibilidade nas buscas e no mapa, além de recursos de marketing e métricas — tudo pensado para gerar oportunidades e negócios em João Pessoa.</p>
      </section>
      <div className="max-w-7xl mx-auto px-4 md:px-8 pb-16 grid sm:grid-cols-3 gap-6">
        {[{n:"Cidade",d:"Feita para João Pessoa e sua gente."},{n:"Tecnologia",d:"Inteligência artificial com dados reais."},{n:"Confiança",d:"Sem avaliações falsas ou conteúdo inventado."}].map((x)=>(
          <div key={x.n} className="bg-slate-50 rounded-2xl p-6"><p className="font-heading font-bold text-slate-900">{x.n}</p><p className="text-sm text-slate-500 mt-1">{x.d}</p></div>
        ))}
      </div>
      <div className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <button onClick={() => navigate("/feed")} className="px-5 py-3 rounded-full bg-brand-gradient text-white font-semibold press inline-flex items-center gap-2">Acessar o App <ArrowRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export function ComoFunciona() {
  const navigate = useNavigate();
  const steps = [
    { icon: Search, t: "Pesquise", d: "Encontre empresas, lugares, eventos e serviços em segundos." },
    { icon: Sparkles, t: "Pergunte à IA", d: "Receba sugestões personalizadas com base em dados reais." },
    { icon: Compass, t: "Descubra", d: "Explore mapas, promoções, praias, turismo e acontecimentos." },
  ];
  const biz = [
    { t: "Cadastre sua empresa", d: "Crie uma presença profissional no Guia de Jampa AI." },
    { t: "Escolha seu plano", d: "Selecione os recursos adequados ao seu negócio." },
    { t: "Receba oportunidades", d: "Gere visibilidade, cliques, contatos e leads." },
  ];
  return (
    <div data-testid="page-como-funciona">
      <SEO title="Como Funciona" description="Veja como o Guia de Jampa AI ajuda moradores, turistas e empresas em João Pessoa." path="/como-funciona" />
      <PageHero eyebrow="Como Funciona" title="João Pessoa em suas mãos" />
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6">Para você</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={s.t} className="bg-white rounded-2xl p-7 soft-shadow">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4"><s.icon className="w-6 h-6 text-primary" /></div>
              <p className="font-heading font-bold text-lg text-slate-900">{i + 1}. {s.t}</p>
              <p className="text-slate-500 mt-2 text-sm">{s.d}</p>
            </div>
          ))}
        </div>
        <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6 mt-14">Para empresas</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {biz.map((s, i) => (
            <div key={s.t} className="bg-slate-50 rounded-2xl p-7">
              <span className="w-9 h-9 rounded-full bg-brand-gradient text-white flex items-center justify-center font-bold">{i + 1}</span>
              <p className="font-heading font-bold text-lg text-slate-900 mt-4">{s.t}</p>
              <p className="text-slate-500 mt-2 text-sm">{s.d}</p>
            </div>
          ))}
        </div>
        <button onClick={() => navigate("/minha-empresa")} className="mt-10 px-5 py-3 rounded-full bg-brand-gradient text-white font-semibold press inline-flex items-center gap-2">Cadastrar minha empresa <ArrowRight className="w-4 h-4" /></button>
      </section>
    </div>
  );
}

export function ParaEmpresas() {
  const navigate = useNavigate();
  const benefits = ["Página empresarial completa", "Presença no mapa", "Botão de WhatsApp", "Produtos e serviços", "Promoções e cupons", "Métricas e leads", "SEO local", "Recomendações da IA", "Destaque conforme o plano"];
  return (
    <div data-testid="page-para-empresas">
      <SEO title="Para Empresas" description="Faça sua empresa ser encontrada em João Pessoa. Página profissional, mapa, WhatsApp, métricas e destaque no Guia de Jampa AI." path="/para-empresas" />
      <PageHero eyebrow="Para Empresas" title="Faça sua empresa ser encontrada em João Pessoa" subtitle="Apareça nas buscas, no mapa e nas recomendações inteligentes do Guia de Jampa AI." />
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {benefits.map((b) => (
            <div key={b} className="flex items-center gap-3 bg-white rounded-2xl p-4 soft-shadow"><Check className="w-5 h-5 text-success shrink-0" /> <span className="text-slate-700">{b}</span></div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <button onClick={() => navigate("/minha-empresa")} className="px-5 py-3 rounded-full bg-brand-gradient text-white font-semibold press inline-flex items-center gap-2">Cadastrar minha empresa <ArrowRight className="w-4 h-4" /></button>
          <button onClick={() => navigate("/planos")} className="px-5 py-3 rounded-full border border-slate-200 text-slate-700 font-semibold hover:border-primary hover:text-primary transition-colors">Ver planos</button>
        </div>
      </section>
    </div>
  );
}

export function Contato() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "", consent: false, hp: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.hp) return; // honeypot
    if (!form.consent) { toast.error("Confirme o consentimento LGPD para enviar."); return; }
    setSending(true);
    try {
      await api.post("/contact", { name: form.name, email: form.email, phone: form.phone, company: form.company, subject: form.subject, message: form.message });
      track("contact_submit");
      toast.success("Mensagem enviada! Em breve entraremos em contato.");
      setSent(true);
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "", consent: false, hp: "" });
    } catch { toast.error("Não foi possível enviar. Tente novamente."); }
    finally { setSending(false); }
  };

  const waText = encodeURIComponent(`Olá! Meu nome é ${form.name || ""}. ${form.message || "Gostaria de mais informações sobre o Guia de Jampa AI."}`);
  const inputCls = "w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-slate-800";

  return (
    <div data-testid="page-contato">
      <SEO title="Contato" description="Fale com o Guia de Jampa AI. Tire dúvidas, cadastre sua empresa ou fale com um especialista." path="/contato" />
      <PageHero eyebrow="Contato" title="Fale com a gente" subtitle="Tire dúvidas, cadastre sua empresa ou fale com um especialista." />
      <section className="max-w-2xl mx-auto px-4 md:px-8 py-14">
        {sent && (
          <div data-testid="contact-success" className="mb-6 rounded-2xl bg-success/10 border border-success/30 p-5 flex items-start gap-3">
            <Check className="w-5 h-5 text-success shrink-0 mt-0.5" />
            <div>
              <p className="font-heading font-semibold text-slate-900">Mensagem recebida com sucesso!</p>
              <p className="text-sm text-slate-600">Obrigado pelo contato. Em breve nossa equipe responderá.</p>
            </div>
          </div>
        )}
        <form onSubmit={submit} data-testid="contact-form" className="grid gap-4">
          <input aria-hidden tabIndex={-1} autoComplete="off" value={form.hp} onChange={set("hp")} className="hidden" />
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="contact-name" required value={form.name} onChange={set("name")} placeholder="Nome*" className={inputCls} />
            <input data-testid="contact-email" required type="email" value={form.email} onChange={set("email")} placeholder="E-mail*" className={inputCls} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="Telefone" className={inputCls} />
            <input data-testid="contact-company" value={form.company} onChange={set("company")} placeholder="Empresa" className={inputCls} />
          </div>
          <input data-testid="contact-subject" value={form.subject} onChange={set("subject")} placeholder="Assunto" className={inputCls} />
          <textarea data-testid="contact-message" required value={form.message} onChange={set("message")} placeholder="Mensagem*" rows={5} className={inputCls} />
          <label className="flex items-start gap-2 text-sm text-slate-600">
            <input data-testid="contact-consent" type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1" />
            Concordo com o tratamento dos meus dados conforme a <a href="/politica-de-privacidade" className="text-primary underline">Política de Privacidade</a> (LGPD).
          </label>
          <div className="flex flex-wrap gap-3">
            <button data-testid="contact-submit" type="submit" disabled={sending} className="px-6 py-3 rounded-full bg-brand-gradient text-white font-semibold press inline-flex items-center gap-2 disabled:opacity-60">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Enviar mensagem
            </button>
            {CONTACT.whatsapp && (
              <a data-testid="contact-whatsapp" href={`https://wa.me/${CONTACT.whatsapp}?text=${waText}`} target="_blank" rel="noopener noreferrer" onClick={() => track("contact_whatsapp")} className="px-6 py-3 rounded-full border border-slate-200 text-slate-700 font-semibold hover:border-primary hover:text-primary transition-colors inline-flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            )}
            <a href={`mailto:${CONTACT.email}`} className="px-6 py-3 rounded-full border border-slate-200 text-slate-700 font-semibold hover:border-primary hover:text-primary transition-colors inline-flex items-center gap-2">
              <Mail className="w-4 h-4" /> {CONTACT.email}
            </a>
          </div>
        </form>
      </section>
    </div>
  );
}

const LEGAL = {
  "politica-de-privacidade": { title: "Política de Privacidade", body: [
    "Esta Política descreve como o Guia de Jampa AI coleta, utiliza e protege os dados pessoais dos usuários, em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei 13.709/2018).",
    "Coletamos dados fornecidos por você (como nome, e-mail e telefone no cadastro e formulários) e dados de uso da plataforma para melhorar sua experiência e oferecer recomendações relevantes.",
    "Não vendemos seus dados. Utilizamos as informações para operar a plataforma, viabilizar contato com empresas, processar pagamentos (via Mercado Pago) e enviar comunicações que você autorizar.",
    "Você pode solicitar acesso, correção ou exclusão dos seus dados a qualquer momento pelo e-mail contato@guiadejampa.com.",
  ] },
  "termos-de-uso": { title: "Termos de Uso", body: [
    "Ao utilizar o Guia de Jampa AI, você concorda com estes Termos de Uso.",
    "A plataforma reúne informações sobre empresas, eventos e serviços de João Pessoa, incluindo conteúdos de fontes públicas. As informações são fornecidas de boa-fé, podendo conter imprecisões; recomendamos confirmar diretamente com o estabelecimento.",
    "As empresas são responsáveis pelas informações que cadastram. É proibido publicar conteúdo ilícito, enganoso ou que viole direitos de terceiros.",
    "Os planos pagos são processados pelo Mercado Pago. Cancelamentos e reembolsos seguem as regras do respectivo plano e da legislação vigente.",
  ] },
  cookies: { title: "Política de Cookies", body: [
    "Utilizamos cookies e tecnologias similares para lembrar preferências, medir audiência e melhorar a experiência de navegação.",
    "Você pode gerenciar os cookies nas configurações do seu navegador. Alguns recursos podem não funcionar corretamente se os cookies forem desativados.",
  ] },
};

export function LegalPage({ doc }) {
  const data = LEGAL[doc];
  if (!data) return null;
  return (
    <div data-testid={`page-legal-${doc}`}>
      <SEO title={data.title} description={`${data.title} do Guia de Jampa AI.`} path={`/${doc}`} />
      <PageHero eyebrow="Institucional" title={data.title} />
      <section className="max-w-3xl mx-auto px-4 md:px-8 py-14 space-y-4 text-slate-600 leading-relaxed">
        {data.body.map((p) => <p key={p}>{p}</p>)}
        <p className="text-sm text-slate-400 pt-4">Última atualização: {new Date().toLocaleDateString("pt-BR")}.</p>
      </section>
    </div>
  );
}
